import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import {
    RemoveInternalMarketerProduct,
    removeInternalMarketerProductAction
} from '../../../../actions/marketer/internal/removeInternalMarketerProduct';
import {
    REMOVE_INTERNAL_MARKETER_PRODUCT_ACCESS,
    RemoveInternalMarketerProductAccess
} from '../../../../actions/marketer/internal/removeInternalMarketerProductAccess';
import {
    UpdateInternalMarketerGridOffset,
    updateInternalMarketerGridOffsetAction
} from '../../../../actions/marketer/internal/updateInternalMarketerGridOffset';
import {
    UpdateInternalMarketerPageStatus,
    updateInternalMarketerPageStatusAction
} from '../../../../actions/marketer/internal/updateInternalMarketerPageStatus';
import {
    UpdateInternalMarketerProduct,
    updateInternalMarketerProductAction
} from '../../../../actions/marketer/internal/updateInternalMarketerProduct';
import { confirm } from '../../../../service/Confirm/Confirm';
import { showNoticeMessage } from '../../../../service/GridHelpers/GridHelpers';
import { EpicDeps } from '../../../../service/StoreFactory';
import { NoticeType } from '../../../../typings/grid';
import { MarketersPageStates } from '../../../../typings/marketers';
import { State } from '../../../../typings/state';

import * as keyset from './removeInternalMarketerProductAccessEpic.i18n';

const i18n = i18nFactory(keyset);

type EpicActions =
    | RemoveInternalMarketerProduct
    | UpdateInternalMarketerPageStatus
    | UpdateInternalMarketerGridOffset
    | UpdateInternalMarketerProduct;

export const removeInternalMarketerProductAccessEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) =>
    action$.pipe(
        ofType(REMOVE_INTERNAL_MARKETER_PRODUCT_ACCESS),
        mergeMap(
            (action: RemoveInternalMarketerProductAccess): Observable<EpicActions> => {
                const {
                    marketer: { ownProducts, id: marketerId }
                } = store$.value.marketerCard;
                const product = ownProducts.find(item => item.id === action.productId);

                if (!product) {
                    return of(updateInternalMarketerPageStatusAction(MarketersPageStates.ERROR));
                }

                return from(
                    confirm({
                        message: i18n('remove-confirm', {
                            product: product.productName
                        })
                    })
                ).pipe(
                    mergeMap(() =>
                        from(deps.marketersDataProvider.removeProductAccess(marketerId, action.productId)).pipe(
                            mergeMap(
                                (success: boolean): Observable<EpicActions> => {
                                    if (success) {
                                        showNoticeMessage(NoticeType.success, i18n('remove-success'));

                                        return of(
                                            removeInternalMarketerProductAction(action.productId),
                                            updateInternalMarketerPageStatusAction(MarketersPageStates.LOADED),
                                            updateInternalMarketerGridOffsetAction(1),
                                            updateInternalMarketerGridOffsetAction(0)
                                        );
                                    }

                                    showNoticeMessage(NoticeType.error, i18n('error'));

                                    return of(
                                        updateInternalMarketerProductAction(product, { isRemoving: false }),
                                        updateInternalMarketerPageStatusAction(MarketersPageStates.PENDING)
                                    );
                                }
                            ),
                            catchError(() => {
                                showNoticeMessage(NoticeType.error, i18n('error'));

                                return of(
                                    updateInternalMarketerProductAction(product, { isRemoving: false }),
                                    updateInternalMarketerPageStatusAction(MarketersPageStates.PENDING)
                                );
                            })
                        )
                    ),
                    catchError(() => of(updateInternalMarketerProductAction(product, { isRemoving: false })))
                );
            }
        )
    );
